import React, { useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  AutoAwesome,
  Info as InfoIcon,
  MenuBook,
  Image as ImageIcon,
} from "@mui/icons-material";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Brand Colors
--------------------------------------------------------------- */
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";
const SUCCESS_GREEN = "#4caf50";
const SUCCESS_BG = "#e8f5e9";

/* ---------------------------------------------------------------
   Fill-in-the-Blank Review Component
--------------------------------------------------------------- */
const FillInBlankReview = ({
  question,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
}) => {
  const qid = question.questionId;
  const isCorrect = answerData?.isCorrect;
  const studentAnswer =
    answerData?.studentAnswer || answerData?.givenAnswer || "—";
  const correctAnswer =
    answerData?.correctAnswer || question.correctAnswer || "—";

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[qid] = el;
      }}
      data-question-id={qid}
      sx={{
        p: 3,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
        }}
      >
        <Chip
          label={qid}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 32,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <HtmlRenderer
            content={question.question}
            sx={{
              fontWeight: 500,
              fontSize: "1rem",
              lineHeight: 1.6,
              color: "#333",
            }}
          />
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
        )}
      </Box>

      {/* Answers */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1.5,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
            Your Answer:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
            }}
          >
            {studentAnswer}
          </Typography>
        </Box>
        {!isCorrect && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Correct Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: SUCCESS_GREEN,
              }}
            >
              {correctAnswer}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Explain Button */}
      {onExplainRequest && !isCorrect && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingExplanations?.[qid] ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesome />
              )
            }
            onClick={() =>
              onExplainRequest(
                qid,
                question.question,
                studentAnswer,
                correctAnswer
              )
            }
            disabled={loadingExplanations?.[qid]}
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "&:hover": {
                borderColor: BRAND_DARK,
                bgcolor: BRAND_BG,
              },
            }}
          >
            Explain
            {explanations?.[qid] && (
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: BRAND_BG,
                  borderLeft: `4px solid ${BRAND_PRIMARY}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#333", lineHeight: 1.6 }}
                >
                  {explanations[qid]}
                </Typography>
              </Paper>
            )}
          </Button>
        </Box>
      )}
    </Card>
  );
};

/* ---------------------------------------------------------------
   MCQ Review Component
--------------------------------------------------------------- */
const MCQReview = ({
  question,
  answerData,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
}) => {
  const qid = question.questionId;
  const options = question.options || [];
  // Some APIs use `correctOption`, others use `correctAnswers` array or `correctAnswer` string
  const correctOption =
    question.correctOption ||
    (Array.isArray(question.correctAnswers) && question.correctAnswers[0]) ||
    question.correctAnswer ||
    null;

  const studentAnswer = (
    answerData?.studentAnswer ||
    answerData?.givenAnswer ||
    ""
  ).toLowerCase();
  const isCorrect = answerData?.isCorrect;

  // Helper to get option letter
  const letterOfIdx = (i) => String.fromCharCode(65 + i);

  // Parse option text - handle both string arrays and object arrays
  const getOptionText = (opt) => {
    if (typeof opt === "string") {
      // Check if it starts with "A. ", "B. ", etc. and strip it
      const match = opt.match(/^[A-D]\.\s*/i);
      if (match) {
        return opt.substring(match[0].length);
      }
      return opt;
    }
    return opt?.text ?? opt?.value ?? opt?.label ?? opt;
  };

  const getOptionLetter = (opt, idx) => {
    if (typeof opt === "string") {
      const match = opt.match(/^([A-D])\./i);
      if (match) {
        return match[1].toLowerCase();
      }
    }
    return letterOfIdx(idx).toLowerCase();
  };

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[qid] = el;
      }}
      data-question-id={qid}
      sx={{
        p: 3,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
        }}
      >
        <Chip
          label={qid}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 32,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <HtmlRenderer
            content={question.question}
            sx={{
              fontWeight: 500,
              fontSize: "1rem",
              lineHeight: 1.6,
              color: "#333",
            }}
          />
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 28 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 28 }} />
        )}
      </Box>

      {/* Options */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2,
          width: "100%",
        }}
      >
        {options.map((opt, idx) => {
          const optLetter = getOptionLetter(opt, idx);
          const optText = getOptionText(opt);
          const displayLetter = letterOfIdx(idx);

          // Check if this option was selected by student
          const isStudentChoice =
            studentAnswer === optLetter ||
            studentAnswer === displayLetter.toLowerCase() ||
            studentAnswer === `text ${optLetter}` ||
            (answerData?.givenAnswer || "").toLowerCase() ===
              optText.toLowerCase();

          // Check if this is the correct option
          const isCorrectChoice =
            correctOption?.toLowerCase() === optLetter ||
            correctOption?.toLowerCase() === displayLetter.toLowerCase();

          let bgColor = "#fff";
          let borderColor = "#e0e0e0";
          let icon = null;

          if (isCorrectChoice) {
            bgColor = SUCCESS_BG;
            borderColor = SUCCESS_GREEN;
            icon = <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />;
          } else if (isStudentChoice && !isCorrectChoice) {
            bgColor = BRAND_BG;
            borderColor = BRAND_PRIMARY;
            icon = <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />;
          }

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                pl: 2,
                borderRadius: 1.5,
                bgcolor: bgColor,
                border: `2px solid ${borderColor}`,
              }}
            >
              {/* Option Letter Circle */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  color: isCorrectChoice
                    ? "#fff"
                    : isStudentChoice
                    ? "#fff"
                    : "#333",
                  bgcolor: isCorrectChoice
                    ? SUCCESS_GREEN
                    : isStudentChoice
                    ? BRAND_PRIMARY
                    : "#fff",
                  border: `2px solid ${
                    isCorrectChoice
                      ? SUCCESS_GREEN
                      : isStudentChoice
                      ? BRAND_PRIMARY
                      : "#ccc"
                  }`,
                }}
              >
                {displayLetter}
              </Box>

              {/* Option Text */}
              <Box sx={{ flex: 1 }}>
                <HtmlRenderer
                  content={optText}
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.5,
                    color: "#333",
                  }}
                />
              </Box>

              {/* Status Icon */}
              {icon}
            </Box>
          );
        })}
      </Box>

      {/* Summary */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1.5,
          border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
            Your Answer:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
              textTransform: "uppercase",
            }}
          >
            {answerData?.givenAnswer ||
              answerData?.studentAnswer ||
              "Not answered"}
          </Typography>
        </Box>
        {!isCorrect && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Correct Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: SUCCESS_GREEN,
                textTransform: "uppercase",
              }}
            >
              {correctOption || answerData?.correctAnswer || "—"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Explain Button */}
      {onExplainRequest && !isCorrect && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingExplanations?.[qid] ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesome />
              )
            }
            onClick={() =>
              onExplainRequest(
                qid,
                question.question,
                studentAnswer,
                correctOption
              )
            }
            disabled={loadingExplanations?.[qid]}
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "&:hover": {
                borderColor: BRAND_DARK,
                bgcolor: BRAND_BG,
              },
            }}
          >
            Explain
          </Button>
          {explanations?.[qid] && (
            <Paper
              sx={{
                mt: 2,
                p: 2,
                bgcolor: BRAND_BG,
                borderLeft: `4px solid ${BRAND_PRIMARY}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#333", lineHeight: 1.6 }}
              >
                {explanations[qid]}
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Card>
  );
};

/* ---------------------------------------------------------------
   Text Display Component (for Part A texts)
--------------------------------------------------------------- */
const TextDisplay = ({ text }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          bgcolor: BRAND_BG,
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MenuBook sx={{ color: BRAND_PRIMARY }} />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
          >
            {text.title}
          </Typography>
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 3 }}>
          {/* Image if present */}
          {text.imageUrl && (
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <img
                src={text.imageUrl}
                alt={text.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: 400,
                  borderRadius: 8,
                  border: "1px solid #e0e0e0",
                }}
              />
            </Box>
          )}

          {/* Paragraph */}
          {text.paragraph && (
            <HtmlRenderer
              content={text.paragraph}
              sx={{
                lineHeight: 1.8,
                color: "#333",
                "& p": { mb: 2 },
                "& ul, & ol": { pl: 3, mb: 2 },
                "& li": { mb: 1 },
              }}
            />
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

/* ---------------------------------------------------------------
   Part Section Component
--------------------------------------------------------------- */
const PartSection = ({
  part,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showTexts, setShowTexts] = useState(false);

  // Debug: inspect normalized part fields (temporary)
  // eslint-disable-next-line no-console
  console.debug("ReadingSectionReview: part incoming:", {
    id: part?._id,
    part: part?.part,
    partName: part?.partName,
    heading: part?.heading,
    order: part?.order,
  });

  // Get part description
  const getPartDescription = (partLabel) => {
    switch (partLabel?.toUpperCase()) {
      case "A":
        return "Expedited Reading - Match information to texts";
      case "B":
        return "Careful Reading - Short extracts with MCQs";
      case "C":
        return "Careful Reading - Long passages with MCQs";
      default:
        return "";
    }
  };

  // Collect all questions from the part
  const allQuestions = useMemo(() => {
    const questions = [];

    // Part A has questions directly under part
    if (part.questions && part.questions.length > 0) {
      questions.push(...part.questions);
    }

    // Parts B and C have texts with questions
    if (part.texts && part.texts.length > 0) {
      part.texts.forEach((text) => {
        if (text.questions && text.questions.length > 0) {
          questions.push(
            ...text.questions.map((q) => ({ ...q, textTitle: text.title }))
          );
        }
      });
    }

    return questions;
  }, [part]);

  // Get answer for a question
  const getAnswerData = (questionId) => {
    return answers?.find((a) => Number(a.questionId) === Number(questionId));
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Part Header */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: BRAND_PRIMARY,
          borderRadius: 2,
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              Part {part.part || part.partName || part.heading || ""}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              {getPartDescription(part.part)}
            </Typography>
          </Box>
          <IconButton sx={{ color: "#fff" }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Card>

      {/* Part Content */}
      <Collapse in={expanded}>
        {/* Instructions */}
        {part.instructions && (
          <Card
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: BRAND_BG,
              border: `1px solid ${BRAND_PRIMARY}20`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <InfoIcon sx={{ color: BRAND_PRIMARY, fontSize: 20, mt: 0.3 }} />
              <HtmlRenderer
                content={part.instructions}
                sx={{
                  fontSize: "0.875rem",
                  color: "#555",
                  lineHeight: 1.6,
                }}
              />
            </Box>
          </Card>
        )}

        {/* Part A - Split Screen Layout */}
        {part.part?.toUpperCase() === "A" &&
          part.texts &&
          part.texts.length > 0 && (
            <Card
              elevation={0}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: BRAND_PRIMARY,
                  color: "#fff",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {part.heading || "Reading Texts & Questions"}
                </Typography>
              </Box>

              {/* Split Screen Container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  minHeight: { lg: "600px" },
                }}
              >
                {/* LEFT SIDE - All Texts */}
                <Box
                  sx={{
                    flex: 1,
                    borderRight: { xs: "none", lg: "1px solid #e0e0e0" },
                    borderBottom: { xs: "1px solid #e0e0e0", lg: "none" },
                    overflow: "auto",
                    maxHeight: { xs: "450px", lg: "750px" },
                    bgcolor: "#fafafa",
                  }}
                >
                  {/* Sticky Header for Text Panel */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      p: 1.5,
                      bgcolor: BRAND_BG,
                      borderBottom: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <MenuBook sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                    >
                      Reference Texts ({part.texts.length})
                    </Typography>
                  </Box>

                  {/* All Texts */}
                  <Box sx={{ p: 2 }}>
                    {part.texts.map((text, idx) => (
                      <Card
                        key={idx}
                        elevation={0}
                        sx={{
                          mb: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          overflow: "hidden",
                          "&:last-child": { mb: 0 },
                        }}
                      >
                        {/* Text Title */}
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#fff",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                          >
                            {text.title}
                          </Typography>
                        </Box>

                        {/* Text Content */}
                        <Box sx={{ p: 2, bgcolor: "#fff" }}>
                          {/* Image if present */}
                          {text.imageUrl && (
                            <Box sx={{ mb: 2, textAlign: "center" }}>
                              <img
                                src={text.imageUrl}
                                alt={text.title}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 300,
                                  borderRadius: 8,
                                  border: "1px solid #e0e0e0",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                }}
                              />
                            </Box>
                          )}

                          {/* Paragraph */}
                          {text.paragraph && (
                            <HtmlRenderer
                              content={text.paragraph}
                              sx={{
                                lineHeight: 1.8,
                                color: "#333",
                                fontSize: "0.9rem",
                                "& p": { mb: 2 },
                                "& ul, & ol": { pl: 3, mb: 2 },
                                "& li": { mb: 1 },
                                "& b, & strong": { color: BRAND_DARK },
                              }}
                            />
                          )}
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>

                {/* RIGHT SIDE - Questions */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    maxHeight: { xs: "none", lg: "750px" },
                    bgcolor: "#fff",
                  }}
                >
                  {/* Sticky Header for Questions Panel */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      p: 1.5,
                      bgcolor: "#fff",
                      borderBottom: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <InfoIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                    >
                      Questions ({part.questions?.length || 0})
                    </Typography>
                  </Box>

                  {/* Questions List */}
                  <Box sx={{ p: 2 }}>
                    {part.questions && part.questions.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        {part.questions.map((question, idx) => {
                          const answerData = getAnswerData(question.questionId);
                          const hasOptions =
                            question.options && question.options.length > 0;

                          if (hasOptions) {
                            return (
                              <MCQReview
                                key={idx}
                                question={question}
                                answerData={answerData}
                                onExplainRequest={onExplainRequest}
                                loadingExplanations={loadingExplanations}
                                explanations={explanations}
                                questionRefs={questionRefs}
                              />
                            );
                          }
                          return (
                            <FillInBlankReview
                              key={idx}
                              question={question}
                              answerData={answerData}
                              onExplainRequest={onExplainRequest}
                              loadingExplanations={loadingExplanations}
                              explanations={explanations}
                              questionRefs={questionRefs}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography
                        sx={{ color: "#666", textAlign: "center", py: 4 }}
                      >
                        No questions available
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          )}

        {/* Texts with Questions for Parts B and C - Split Screen Layout */}
        {part.texts &&
          part.texts.length > 0 &&
          (part.part?.toUpperCase() === "B" ||
            part.part?.toUpperCase() === "C") && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {part.texts.map((text, textIdx) => (
                <Card
                  key={textIdx}
                  elevation={0}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  {/* Text Title Header */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: BRAND_PRIMARY,
                      color: "#fff",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {text.title}
                    </Typography>
                  </Box>

                  {/* Split Screen Container */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", lg: "row" },
                      minHeight: { lg: "600px" },
                    }}
                  >
                    {/* LEFT SIDE - Text/Passage */}
                    <Box
                      sx={{
                        flex: 1,
                        borderRight: { xs: "none", lg: "1px solid #e0e0e0" },
                        borderBottom: { xs: "1px solid #e0e0e0", lg: "none" },
                        overflow: "auto",
                        maxHeight: { xs: "400px", lg: "700px" },
                        bgcolor: "#fafafa",
                      }}
                    >
                      {/* Sticky Header for Text Panel */}
                      <Box
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          p: 1.5,
                          bgcolor: BRAND_BG,
                          borderBottom: "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <MenuBook sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                        >
                          Reading Passage
                        </Typography>
                      </Box>

                      {/* Text Content */}
                      <Box sx={{ p: 3 }}>
                        {/* Image if present */}
                        {text.imageUrl && (
                          <Box sx={{ mb: 3, textAlign: "center" }}>
                            <img
                              src={text.imageUrl}
                              alt={text.title}
                              style={{
                                maxWidth: "100%",
                                maxHeight: 350,
                                borderRadius: 8,
                                border: "1px solid #e0e0e0",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            />
                          </Box>
                        )}

                        {/* Paragraph */}
                        {text.paragraph && (
                          <HtmlRenderer
                            content={text.paragraph}
                            sx={{
                              lineHeight: 1.9,
                              color: "#333",
                              fontSize: "0.95rem",
                              "& p": { mb: 2 },
                              "& ul, & ol": { pl: 3, mb: 2 },
                              "& li": { mb: 1 },
                              "& b, & strong": { color: BRAND_DARK },
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* RIGHT SIDE - Questions */}
                    <Box
                      sx={{
                        flex: 1,
                        overflow: "auto",
                        maxHeight: { xs: "none", lg: "700px" },
                        bgcolor: "#fff",
                      }}
                    >
                      {/* Sticky Header for Questions Panel */}
                      <Box
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          p: 1.5,
                          bgcolor: "#fff",
                          borderBottom: "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <InfoIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                        >
                          Questions ({text.questions?.length || 0})
                        </Typography>
                      </Box>

                      {/* Questions List */}
                      <Box sx={{ p: 2 }}>
                        {text.questions && text.questions.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            {text.questions.map((question, qIdx) => {
                              const answerData = getAnswerData(
                                question.questionId
                              );
                              const hasOptions =
                                question.options && question.options.length > 0;

                              if (hasOptions) {
                                return (
                                  <MCQReview
                                    key={qIdx}
                                    question={question}
                                    answerData={answerData}
                                    onExplainRequest={onExplainRequest}
                                    loadingExplanations={loadingExplanations}
                                    explanations={explanations}
                                    questionRefs={questionRefs}
                                  />
                                );
                              }
                              return (
                                <FillInBlankReview
                                  key={qIdx}
                                  question={question}
                                  answerData={answerData}
                                  onExplainRequest={onExplainRequest}
                                  loadingExplanations={loadingExplanations}
                                  explanations={explanations}
                                  questionRefs={questionRefs}
                                />
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography
                            sx={{ color: "#666", textAlign: "center", py: 4 }}
                          >
                            No questions for this text
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
      </Collapse>
    </Box>
  );
};

/* ---------------------------------------------------------------
   Main Component
--------------------------------------------------------------- */
export default function ReadingSectionReview({
  readingData,
  answers = [],
  onExplainRequest,
  loadingExplanations = {},
  explanations = {},
}) {
  // Refs for scrolling to questions
  const questionRefs = useRef({});

  // Get parts from reading data
  const parts = useMemo(() => {
    if (readingData?.parts && Array.isArray(readingData.parts)) {
      return readingData.parts;
    }
    if (
      readingData?.oetReadingPart &&
      Array.isArray(readingData.oetReadingPart)
    ) {
      return readingData.oetReadingPart;
    }
    return [];
  }, [readingData]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = answers.length;
    const correct = answers.filter((a) => a.isCorrect).length;
    const wrong = total - correct;
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    return { total, correct, wrong, percentage };
  }, [answers]);

  // Get all question IDs in order for chips
  const allQuestionIds = useMemo(() => {
    const ids = [];
    parts.forEach((part) => {
      // Part A style - questions directly on part
      if (part.questions && Array.isArray(part.questions)) {
        part.questions.forEach((q) => {
          if (q.questionId) ids.push(q.questionId);
        });
      }
      // Part B/C style - questions in texts array
      if (part.texts && Array.isArray(part.texts)) {
        part.texts.forEach((text) => {
          if (text.questions && Array.isArray(text.questions)) {
            text.questions.forEach((q) => {
              if (q.questionId) ids.push(q.questionId);
            });
          }
        });
      }
    });
    // Deduplicate while preserving order, in case some parts accidentally included the same question multiple times
    const uniq = [];
    const seen = new Set();
    ids.forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        uniq.push(id);
      }
    });
    return uniq;
  }, [parts]);

  // Map question IDs to their answer data for chips
  const questionAnswerMap = useMemo(() => {
    const map = {};
    answers.forEach((ans) => {
      const qid = ans.questionId || ans.question_id;
      if (qid) map[qid] = ans;
    });
    return map;
  }, [answers]);

  // Handle chip click - scroll to question
  const handleChipClick = useCallback((questionId) => {
    const element = questionRefs.current[questionId];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // Highlight effect
      element.style.transition = "box-shadow 0.3s ease";
      element.style.boxShadow = `0 0 0 3px ${BRAND_PRIMARY}`;
      setTimeout(() => {
        element.style.boxShadow = "none";
      }, 1500);
    }
  }, []);

  if (!parts.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No reading data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", bgcolor: "#fff" }}>
      {/* Sticky Header with Question Chips */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          {/* Title and Stats Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: BRAND_PRIMARY,
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                }}
              >
                Reading Module Review
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#666",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                }}
              >
                Review your answers
              </Typography>
            </Box>
          </Box>

          {/* Question Chips - Clickable Circular */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {allQuestionIds.map((qid, idx) => {
              const answerData = questionAnswerMap[qid];
              const isCorrect = answerData?.isCorrect;
              return (
                <Box
                  key={qid}
                  onClick={() => handleChipClick(qid)}
                  sx={{
                    width: { xs: 22, sm: 26 },
                    height: { xs: 22, sm: 26 },
                    borderRadius: "50%",
                    border: `2px solid ${
                      isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY
                    }`,
                    bgcolor: "#fff",
                    color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      bgcolor: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                      color: "#fff",
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  {idx + 1}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Part Sections */}
      <Box sx={{ p: { xs: 1, sm: 1.5 }, width: "100%" }}>
        {parts.map((part, idx) => (
          <PartSection
            key={idx}
            part={part}
            answers={answers}
            onExplainRequest={onExplainRequest}
            loadingExplanations={loadingExplanations}
            explanations={explanations}
            questionRefs={questionRefs}
          />
        ))}
      </Box>
    </Box>
  );
}
